// Every mark the page sees, kept per market as a time series for the live chart: at most one point
// each 100 ms, the last two hours. Prices here are numbers for drawing only; no figure is counted
// from them.

import { useEffect } from "react";
import { fromRaw, scalePlaces } from "../lib/num";
import { marks, scales, type Mark } from "./market";

export type Tick = { t: number; p: number };

const KEEP_MS = 2 * 60 * 60 * 1000;
const STEP_MS = 100;

const series = new Map<number, Tick[]>();

/** The ticks of `instrumentId` since the page opened, oldest first. Read, never mutated. */
export function ticksOf(instrumentId: number): readonly Tick[] {
  return series.get(instrumentId) ?? NONE;
}
const NONE: readonly Tick[] = [];

/** A raw mark as the chart's number. */
export function markNumber(raw: string, scale: string): number {
  return Number(fromRaw(raw, scalePlaces(scale)));
}

function record(all: Record<number, Mark>) {
  for (const [key, mark] of Object.entries(all)) {
    const id = Number(key);
    const scale = scales.get(id);
    if (!scale || mark.source === "frozen") continue;
    const p = markNumber(mark.raw, scale);
    if (!Number.isFinite(p)) continue;
    const list = series.get(id) ?? [];
    const last = list.at(-1);
    const t = Math.max(mark.at, last?.t ?? 0);
    if (last && t - last.t < STEP_MS) {
      last.p = p;
    } else if (!last || last.p !== p || t - last.t > 1_000) {
      list.push({ t, p });
    }
    if (list.length > 0 && t - (list[0]?.t ?? t) > KEEP_MS + 60_000) {
      const cut = list.findIndex((tick) => t - tick.t <= KEEP_MS);
      if (cut > 0) list.splice(0, cut);
    }
    series.set(id, list);
  }
}

/** Keeps the series: mounted once by the app. */
export function useTickRecorder() {
  useEffect(() => {
    record(marks.get());
    return marks.subscribe(() => record(marks.get()));
  }, []);
}
